import { JSX } from 'react';

/** 处理行内加粗 **text** */
function processInline(str: string): JSX.Element {
  const parts: (string | JSX.Element)[] = [];
  const regex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let keyCounter = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(str)) !== null) {
    if (match.index > lastIndex) {
      parts.push(str.slice(lastIndex, match.index));
    }
    parts.push(
      <strong key={`b-${keyCounter++}`} className="text-foreground-900 font-semibold">
        {match[1]}
      </strong>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < str.length) {
    parts.push(str.slice(lastIndex));
  }
  return <span>{parts}</span>;
}

/** 轻量 Markdown 渲染器，支持 ### 标题、加粗、无序/有序列表 */
export default function MarkdownContent({ text }: { text: string }): JSX.Element {
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  let currentList: JSX.Element[] = [];
  let listType: 'ul' | 'ol' | null = null;

  const flushList = () => {
    if (currentList.length === 0) return;
    if (listType === 'ul') {
      elements.push(
        <ul key={`ul-${elements.length}`} className="space-y-1 ml-2 mb-3">
          {currentList}
        </ul>,
      );
    } else if (listType === 'ol') {
      elements.push(
        <ol key={`ol-${elements.length}`} className="space-y-1 ml-4 mb-3 list-decimal">
          {currentList}
        </ol>,
      );
    }
    currentList = [];
    listType = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === '') {
      flushList();
      continue;
    }

    // ### 标题
    if (trimmed.startsWith('### ')) {
      flushList();
      const content = trimmed.slice(4);
      elements.push(
        <h3
          key={`h3-${i}`}
          className="font-heading text-base font-semibold text-primary-700 mt-4 mb-2 border-l-4 border-primary-400 pl-3"
        >
          {processInline(content)}
        </h3>,
      );
      continue;
    }

    // 整行加粗标题如 **一、命局总览**
    if (
      trimmed.startsWith('**') &&
      trimmed.endsWith('**') &&
      trimmed.indexOf('**', 2) === trimmed.length - 2
    ) {
      flushList();
      const content = trimmed.slice(2, -2);
      elements.push(
        <h3
          key={`bold-h-${i}`}
          className="font-heading text-base font-semibold text-primary-700 mt-4 mb-2 border-l-4 border-primary-400 pl-3"
        >
          {content}
        </h3>,
      );
      continue;
    }

    // 无序列表 -
    if (trimmed.startsWith('- ')) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      currentList.push(
        <li
          key={`li-${i}`}
          className="text-sm text-foreground-700 leading-relaxed flex items-start gap-2"
        >
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-secondary-400 flex-shrink-0" />
          {processInline(trimmed.slice(2))}
        </li>,
      );
      continue;
    }

    // 有序列表 1. 2. 3.
    if (/^\d+\.\s/.test(trimmed)) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      const content = trimmed.replace(/^\d+\.\s/, '');
      currentList.push(
        <li
          key={`oli-${i}`}
          className="text-sm text-foreground-700 leading-relaxed"
        >
          {processInline(content)}
        </li>,
      );
      continue;
    }

    // 普通段落
    flushList();
    elements.push(
      <p key={`p-${i}`} className="text-sm text-foreground-700 leading-relaxed mb-2">
        {processInline(trimmed)}
      </p>,
    );
  }

  flushList();
  return <div>{elements}</div>;
}